'use client'

import { EntityCatalogTab } from '@/components/catalog/EntityCatalogTab'
import AddressList from '@/components/address/address-list'
import ConfirmModal from '@/components/confirm-modal'
import { PayoutScheduleDisplay } from '@/components/payout-schedule/PayoutScheduleDisplay'
import NotificationPreferencesCard from '@/components/notification-preferences/notification-preferences-card'
import { getFileUrl } from '@/lib/utils/file-url'
import { Billing, BillingStatus } from '@/lib/api/common/interfaces/billing.interface'
import { Partner } from '@/lib/api/common/interfaces/partner.interface'
import { getBillings } from '@/lib/api/financial/financial-billing-api'
import { deletePartner, getPartner, getPartnerTransferTargets, PartnerTransferTarget, regeneratePartnerAccessCode, sendPartnerAccessCode, togglePartnerActive, transferPartnerCompany, uploadPartnerLogo } from '@/lib/api/partners/partners-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
    MdArrowBack,
    MdBusiness,
    MdCloudUpload,
    MdContactPhone,
    MdDelete,
    MdEdit,
    MdEmail,
    MdImage,
    MdInventory2,
    MdLink,
    MdLocationOn,
    MdPayment,
    MdPerson,
    MdPhone,
    MdReceipt,
    MdSwapHoriz,
    MdToggleOff,
    MdToggleOn,
    MdWarningAmber,
    MdContentCopy,
    MdVpnKey
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

const STATUS_LABELS: Record<BillingStatus, string> = {
    PENDING: 'Pendente',
    PAID: 'Pago',
    OVERDUE: 'Vencido',
    CANCELLED: 'Cancelado',
    REFUNDED: 'Estornado',
    PARTIALLY_REFUNDED: 'Parcialmente Estornado',
    PARTIALLY_PAID: 'Parcial',
    IN_DISPUTE: 'Em disputa',
    WRITTEN_OFF: 'Baixado',
}

const STATUS_BADGE_CLASS: Record<BillingStatus, string> = {
    PENDING: 'badge-warning',
    PAID: 'badge-success text-white',
    OVERDUE: 'badge-error text-white',
    CANCELLED: 'badge-ghost',
    REFUNDED: 'badge-info text-white',
    PARTIALLY_REFUNDED: 'badge-info',
    PARTIALLY_PAID: 'badge-warning',
    IN_DISPUTE: 'badge-error text-white',
    WRITTEN_OFF: 'badge-ghost',
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
    PIX: 'PIX',
    BOLETO: 'Boleto',
    CREDIT_CARD: 'Cartão de Crédito',
    DEBIT_CARD: 'Cartão de Débito',
    CASH: 'Dinheiro',
    BANK_TRANSFER: 'Transferência',
    CHECK: 'Cheque',
    OTHER: 'Outro',
}

export default function PartnerDetailPage() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { id } = useParams<{ id: string }>()
    const { canUpdate, canDelete } = useResourcePermissions('person', 'partners')
    const [partner, setPartner] = useState<Partner | null>(null)
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState<'dados' | 'catalog'>('dados')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [uploadingLogo, setUploadingLogo] = useState(false)
    const [logoPreview, setLogoPreview] = useState<string | null>(null)
    const [billings, setBillings] = useState<Billing[]>([])
    const [loadingBillings, setLoadingBillings] = useState(false)
    const [sendingCode, setSendingCode] = useState(false)
    const [showRegenerateModal, setShowRegenerateModal] = useState(false)
    const [regenerating, setRegenerating] = useState(false)
    const [showTransferModal, setShowTransferModal] = useState(false)
    const [transferTargets, setTransferTargets] = useState<PartnerTransferTarget[]>([])
    const [loadingTargets, setLoadingTargets] = useState(false)
    const [selectedTargetId, setSelectedTargetId] = useState<number | ''>('')
    const [transferring, setTransferring] = useState(false)
    const logoInputRef = useRef<HTMLInputElement>(null)

    const loadBillings = useCallback(async (partnerId: number) => {
        setLoadingBillings(true)
        try {
            const response = await getBillings({ partnerId, limit: 50 })
            if (response.success && response.result) {
                setBillings(response.result)
            }
        } catch {
            // silencioso
        } finally {
            setLoadingBillings(false)
        }
    }, [])

    useEffect(() => {
        if (id) {
            loadPartner()
        }
    }, [id])

    const loadPartner = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getPartner(parseInt(id, 10))
            if (response.success && response.result) {
                setPartner(response.result)
                loadBillings(response.result.id)
            } else {
                toast.error('Parceiro não encontrado')
                navigate('/person/partners')
            }
        } catch (error) {
            toast.error('Erro ao carregar parceiro')
            navigate('/person/partners')
        } finally {
            setLoading(false)
        }
    }

    const handleToggleActive = async () => {
        if (!partner) return

        try {
            const response = await togglePartnerActive(partner.id)
            if (response.success) {
                toast.success(
                    partner.active
                        ? 'Parceiro desativado com sucesso'
                        : 'Parceiro ativado com sucesso'
                )
                await loadPartner()
            } else {
                toast.error(response.error || 'Erro ao alterar status')
            }
        } catch (error) {
            toast.error('Erro ao alterar status')
        }
    }

    const handleDelete = async () => {
        if (!partner) return

        setDeleting(true)
        try {
            const response = await deletePartner(partner.id)
            if (response.success) {
                toast.success('Parceiro excluído com sucesso')
                navigate('/person/partners')
            } else {
                toast.error(response.error || 'Erro ao excluir parceiro')
            }
        } catch (error) {
            toast.error('Erro ao excluir parceiro')
        } finally {
            setDeleting(false)
        }
    }

    const handleLogoUpload = async (file: File) => {
        if (!partner) return

        setUploadingLogo(true)
        try {
            const response = await uploadPartnerLogo(partner.id, file)
            if (response.success && response.result) {
                toast.success('Logo atualizada com sucesso')
                setLogoPreview(null)
                setPartner(prev =>
                    prev ? { ...prev, logoUrl: response.result.logoUrl } : prev
                )
            } else {
                toast.error(response.error || 'Erro ao enviar logo')
            }
        } catch {
            toast.error('Erro ao enviar logo')
        } finally {
            setUploadingLogo(false)
        }
    }

    const handleSendAccessCode = async () => {
        if (!partner) return
        setSendingCode(true)
        try {
            const response = await sendPartnerAccessCode(partner.id)
            if (response.success) {
                toast.success('Código de acesso enviado por e-mail')
            } else {
                toast.error(response.error || 'Erro ao enviar código')
            }
        } catch {
            toast.error('Erro ao enviar código')
        } finally {
            setSendingCode(false)
        }
    }

    const handleRegenerateAccessCode = async () => {
        if (!partner) return
        setRegenerating(true)
        try {
            const response = await regeneratePartnerAccessCode(partner.id)
            if (response.success && response.result) {
                toast.success('Novo código de acesso gerado com sucesso')
                setPartner(prev =>
                    prev ? { ...prev, accessCode: response.result!.accessCode } : prev
                )
                setShowRegenerateModal(false)
            } else {
                toast.error(response.error || 'Erro ao gerar novo código')
            }
        } catch {
            toast.error('Erro ao gerar novo código')
        } finally {
            setRegenerating(false)
        }
    }

    const handleOpenTransferModal = async () => {
        if (!partner) return

        setShowTransferModal(true)
        setSelectedTargetId('')
        setLoadingTargets(true)
        try {
            const response = await getPartnerTransferTargets(partner.id)
            if (response.success && response.result) {
                setTransferTargets(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar filiais')
            }
        } catch {
            toast.error('Erro ao carregar filiais')
        } finally {
            setLoadingTargets(false)
        }
    }

    const handleTransfer = async () => {
        if (!partner || !selectedTargetId) return

        setTransferring(true)
        try {
            const response = await transferPartnerCompany(partner.id, selectedTargetId)
            if (response.success && response.result) {
                const { totalMoved } = response.result
                toast.success(
                    totalMoved > 0
                        ? `Parceiro transferido com sucesso. ${totalMoved} registro(s) movido(s) para a nova filial.`
                        : 'Parceiro transferido com sucesso.',
                )
                setShowTransferModal(false)
                await loadPartner()
            } else {
                toast.error(response.error || 'Erro ao transferir parceiro')
            }
        } catch {
            toast.error('Erro ao transferir parceiro')
        } finally {
            setTransferring(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando parceiro...
                </p>
            </div>
        )
    }

    if (!partner) {
        return null
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/person/partners')}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg overflow-hidden">
                            {partner.logoUrl ? (
                                <img
                                    src={getFileUrl(partner.logoUrl)}
                                    alt="Logo"
                                    className="w-12 h-12 object-contain"
                                />
                            ) : partner.personType === 'JURIDICA' ? (
                                <MdBusiness className="text-2xl text-white" />
                            ) : (
                                <MdPerson className="text-2xl text-white" />
                            )}
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-base-content">
                                {partner.name}
                            </h1>
                            {partner.tradingName && (
                                <p className="text-sm text-base-content/60">
                                    {partner.tradingName}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {(canUpdate || canDelete) && (
                    <div className="flex items-center gap-2">
                        {canUpdate && (
                            <button
                                onClick={handleToggleActive}
                                className={`btn gap-2 ${partner.active ? 'btn-warning' : 'btn-success'
                                    }`}
                            >
                                {partner.active ? (
                                    <>
                                        <MdToggleOff className="text-xl" />
                                        <span className="hidden sm:inline">Desativar</span>
                                    </>
                                ) : (
                                    <>
                                        <MdToggleOn className="text-xl" />
                                        <span className="hidden sm:inline">Ativar</span>
                                    </>
                                )}
                            </button>
                        )}
                        {canUpdate && (
                            <button
                                onClick={handleOpenTransferModal}
                                className="btn btn-outline gap-2"
                                title="Mover o parceiro para outra filial"
                            >
                                <MdSwapHoriz className="text-xl" />
                                <span className="hidden sm:inline">Mover de filial</span>
                            </button>
                        )}
                        {canUpdate && (
                            <button
                                onClick={() => navigate(`/person/partners/edit/${partner.id}`)}
                                className="btn btn-secondary gap-2"
                            >
                                <MdEdit className="text-xl" />
                                <span className="hidden sm:inline">Editar</span>
                            </button>
                        )}
                        {canDelete && (
                            <button
                                onClick={() => setShowDeleteModal(true)}
                                className="btn btn-error gap-2"
                            >
                                <MdDelete className="text-xl" />
                                <span className="hidden sm:inline">Excluir</span>
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div role="tablist" className="tabs tabs-bordered">
                <button
                    role="tab"
                    className={`tab gap-2 ${activeTab === 'dados' ? 'tab-active' : ''}`}
                    onClick={() => setActiveTab('dados')}
                >
                    <MdPerson className="text-lg" /> Dados
                </button>
                <button
                    role="tab"
                    className={`tab gap-2 ${activeTab === 'catalog' ? 'tab-active' : ''}`}
                    onClick={() => setActiveTab('catalog')}
                >
                    <MdInventory2 className="text-lg" /> Catálogo do Parceiro
                </button>
            </div>

            {activeTab === 'catalog' &&
                (user?.companyId ? (
                    <EntityCatalogTab
                        entityType="partner"
                        entityId={partner.id}
                        companyId={user.companyId}
                        entityName={partner.name || partner.tradingName || undefined}
                        entityLocation={[partner.city, partner.state]
                            .filter(Boolean)
                            .join(' - ')}
                    />
                ) : (
                    <p className="text-center py-10 text-base-content/60">
                        Empresa do usuário não identificada.
                    </p>
                ))}

            <div
                className={
                    activeTab === 'dados' ? 'flex flex-col gap-6' : 'hidden'
                }
            >
            <div className="flex items-center gap-2">
                <span
                    className={`badge ${partner.active ? 'badge-success' : 'badge-error'
                        } text-white`}
                >
                    {partner.active ? 'Ativo' : 'Inativo'}
                </span>
                <span className="badge badge-outline">
                    {partner.personType === 'JURIDICA'
                        ? 'Pessoa Jurídica'
                        : 'Pessoa Física'}
                </span>
                {partner.company && (
                    <span className="badge badge-ghost gap-1">
                        <MdBusiness className="text-sm" />
                        {partner.company.tradingName || partner.company.name}
                    </span>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Informacoes de Contato */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Informações de Contato
                    </h2>
                    <div className="space-y-4">
                        {partner.email && (
                            <div className="flex items-center gap-3">
                                <MdEmail className="text-xl text-base-content/50" />
                                <div>
                                    <p className="text-xs text-base-content/50">Email</p>
                                    <a
                                        href={`mailto:${partner.email}`}
                                        className="text-secondary hover:underline"
                                    >
                                        {partner.email}
                                    </a>
                                </div>
                            </div>
                        )}
                        {partner.phone && (
                            <div className="flex items-center gap-3">
                                <MdPhone className="text-xl text-base-content/50" />
                                <div>
                                    <p className="text-xs text-base-content/50">Telefone</p>
                                    <a
                                        href={`tel:${partner.phone}`}
                                        className="text-secondary hover:underline"
                                    >
                                        {partner.phone}
                                    </a>
                                </div>
                            </div>
                        )}
                        {partner.website && (
                            <div className="flex items-center gap-3">
                                <MdLink className="text-xl text-base-content/50" />
                                <div>
                                    <p className="text-xs text-base-content/50">Website</p>
                                    <a
                                        href={partner.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-secondary hover:underline"
                                    >
                                        {partner.website}
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Documentos */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Documentos
                    </h2>
                    <div className="space-y-4">
                        {partner.personType === 'JURIDICA' ? (
                            <>
                                {partner.cnpj && (
                                    <div>
                                        <p className="text-xs text-base-content/50">CNPJ</p>
                                        <p className="font-medium">{partner.cnpj}</p>
                                    </div>
                                )}
                                {partner.stateRegistration && (
                                    <div>
                                        <p className="text-xs text-base-content/50">
                                            Inscrição Estadual
                                        </p>
                                        <p className="font-medium">{partner.stateRegistration}</p>
                                    </div>
                                )}
                                {partner.municipalRegistration && (
                                    <div>
                                        <p className="text-xs text-base-content/50">
                                            Inscrição Municipal
                                        </p>
                                        <p className="font-medium">{partner.municipalRegistration}</p>
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {partner.cpf && (
                                    <div>
                                        <p className="text-xs text-base-content/50">CPF</p>
                                        <p className="font-medium">{partner.cpf}</p>
                                    </div>
                                )}
                                {partner.rg && (
                                    <div>
                                        <p className="text-xs text-base-content/50">
                                            RG {partner.rgIssuer && `(${partner.rgIssuer})`}
                                        </p>
                                        <p className="font-medium">{partner.rg}</p>
                                    </div>
                                )}
                            </>
                        )}
                        {!partner.cnpj && !partner.cpf && (
                            <p className="text-base-content/50 text-sm">
                                Nenhum documento cadastrado
                            </p>
                        )}
                    </div>
                </div>

                {/* Endereco */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                        <MdLocationOn className="text-xl" />
                        Endereço
                    </h2>
                    <AddressList
                        owner={{ partnerId: partner.id }}
                        emptyHint="Nenhum endereço cadastrado para este parceiro"
                    />
                </div>

                {/* Contato Adicional */}
                {(partner.contactName || partner.contactPhone || partner.contactEmail) && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                            <MdContactPhone className="text-xl" />
                            Contato Adicional
                        </h2>
                        <div className="space-y-2">
                            {partner.contactName && (
                                <div>
                                    <p className="text-xs text-base-content/50">Nome</p>
                                    <p className="font-medium">{partner.contactName}</p>
                                </div>
                            )}
                            {partner.contactPhone && (
                                <div>
                                    <p className="text-xs text-base-content/50">Telefone</p>
                                    <a
                                        href={`tel:${partner.contactPhone}`}
                                        className="text-secondary hover:underline"
                                    >
                                        {partner.contactPhone}
                                    </a>
                                </div>
                            )}
                            {partner.contactEmail && (
                                <div>
                                    <p className="text-xs text-base-content/50">Email</p>
                                    <a
                                        href={`mailto:${partner.contactEmail}`}
                                        className="text-secondary hover:underline"
                                    >
                                        {partner.contactEmail}
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Dados Bancarios 
                {(partner.bankName || partner.pixKey) && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                            <MdAccountBalance className="text-xl" />
                            Dados Bancários
                        </h2>
                        <div className="space-y-2">
                            {partner.bankName && (
                                <div>
                                    <p className="text-xs text-base-content/50">Banco</p>
                                    <p className="font-medium">
                                        {partner.bankCode && `${partner.bankCode} - `}
                                        {partner.bankName}
                                    </p>
                                </div>
                            )}
                            {partner.bankAgency && (
                                <div>
                                    <p className="text-xs text-base-content/50">Agência</p>
                                    <p className="font-medium">{partner.bankAgency}</p>
                                </div>
                            )}
                            {partner.bankAccount && (
                                <div>
                                    <p className="text-xs text-base-content/50">Conta</p>
                                    <p className="font-medium">{partner.bankAccount}</p>
                                </div>
                            )}
                            {partner.pixKey && (
                                <div>
                                    <p className="text-xs text-base-content/50">
                                        Chave PIX {partner.pixKeyType && `(${partner.pixKeyType})`}
                                    </p>
                                    <p className="font-medium">{partner.pixKey}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
*/}
                {/* Repasse de Valores */}
                <div className="lg:col-span-2">
                    <PayoutScheduleDisplay
                        payoutScheduleType={partner.payoutScheduleType}
                        payoutFixedDay={partner.payoutFixedDay}
                        payoutDaysAfterAppointment={partner.payoutDaysAfterAppointment}
                    />
                </div>

                {/* Notificações: por quais canais este parceiro aceita ser avisado.
                    Fica no detalhe, e não na edição, porque é decisão de
                    relacionamento — e vale na hora, sem salvar a tela inteira. */}
                <div className="lg:col-span-2">
                    <NotificationPreferencesCard
                        entityType="partner"
                        entityId={partner.id}
                        preferences={(partner as any).notificationPreferences}
                        entityName={partner.tradingName || partner.name}
                        canEdit={canUpdate}
                    />
                </div>

                {/* Observacoes */}
                {partner.notes && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6 lg:col-span-2">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Observações
                        </h2>
                        <p className="text-base-content whitespace-pre-wrap">
                            {partner.notes}
                        </p>
                    </div>
                )}

                {/* Logo */}
                {canUpdate && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                            <MdImage className="text-xl" />
                            Logo do Parceiro
                        </h2>
                        <div className="flex items-center gap-6">
                            <div className="w-24 h-24 rounded-lg bg-base-100 border-2 border-dashed border-base-300 flex items-center justify-center overflow-hidden">
                                {logoPreview || partner.logoUrl ? (
                                    <img
                                        src={
                                            logoPreview ||
                                            getFileUrl(partner.logoUrl)
                                        }
                                        alt="Logo"
                                        className="max-w-full max-h-full object-contain"
                                    />
                                ) : (
                                    <MdImage className="text-3xl text-base-content/30" />
                                )}
                            </div>
                            <div className="flex flex-col gap-2">
                                <input
                                    ref={logoInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={e => {
                                        const file = e.target.files?.[0]
                                        if (file) {
                                            const reader = new FileReader()
                                            reader.onload = () =>
                                                setLogoPreview(reader.result as string)
                                            reader.readAsDataURL(file)
                                            handleLogoUpload(file)
                                        }
                                    }}
                                />
                                <button
                                    className="btn btn-secondary btn-sm gap-2"
                                    onClick={() => logoInputRef.current?.click()}
                                    disabled={uploadingLogo}
                                >
                                    {uploadingLogo ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        <MdCloudUpload className="text-lg" />
                                    )}
                                    {uploadingLogo ? 'Enviando...' : 'Enviar Logo'}
                                </button>
                                <p className="text-xs text-base-content/50">
                                    PNG, JPG ou SVG. Tamanho máximo: 5MB.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Código de Acesso ao Portal */}
                {canUpdate && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                            <MdVpnKey className="text-xl" />
                            Código de Acesso ao Portal
                        </h2>
                        <div className="space-y-4">
                            <div>
                                <p className="text-xs text-base-content/50">Código de acesso</p>
                                <div className="flex items-center gap-2 mt-1">
                                    <code className="text-sm font-mono bg-base-200 px-3 py-1.5 rounded-lg inline-block select-all">
                                        {partner.accessCode || 'Não gerado'}
                                    </code>
                                    {partner.accessCode && (
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-xs btn-square"
                                            title="Copiar código"
                                            onClick={() => {
                                                navigator.clipboard.writeText(partner.accessCode!)
                                                toast.success('Código copiado!')
                                            }}
                                        >
                                            <MdContentCopy className="text-base" />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    className="btn btn-sm btn-secondary gap-2"
                                    onClick={handleSendAccessCode}
                                    disabled={sendingCode || !partner.email}
                                    title={!partner.email ? 'Parceiro não possui e-mail cadastrado' : ''}
                                >
                                    {sendingCode ? (
                                        <span className="loading loading-spinner loading-xs" />
                                    ) : (
                                        <MdEmail className="text-lg" />
                                    )}
                                    Enviar por e-mail
                                </button>
                                <button
                                    className="btn btn-sm btn-warning gap-2"
                                    onClick={() => setShowRegenerateModal(true)}
                                >
                                    <MdVpnKey className="text-lg" />
                                    Gerar novo código
                                </button>
                            </div>
                            {!partner.email && (
                                <p className="text-xs text-warning">
                                    Cadastre um e-mail para poder enviar o código de acesso.
                                </p>
                            )}
                        </div>
                    </div>
                )}

            </div>
            </div>

            {showTransferModal && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-lg">
                        <h3 className="text-lg font-bold flex items-center gap-2">
                            <MdSwapHoriz className="text-2xl text-secondary" />
                            Mover parceiro de filial
                        </h3>

                        <p className="mt-3 text-sm text-base-content/70">
                            O parceiro <strong>{partner.name || partner.tradingName}</strong>{' '}
                            será movido da filial{' '}
                            <strong>
                                {partner.company?.tradingName ||
                                    partner.company?.name ||
                                    'atual'}
                            </strong>{' '}
                            para a filial escolhida abaixo.
                        </p>

                        <div className="form-control w-full mt-6">
                            <label className="label pb-2" htmlFor="transfer-target">
                                <span className="label-text font-medium">
                                    Filial de destino
                                </span>
                            </label>
                            {loadingTargets ? (
                                <div className="flex items-center gap-2 py-3 text-sm text-base-content/60">
                                    <span className="loading loading-spinner loading-sm" />
                                    Carregando filiais...
                                </div>
                            ) : transferTargets.length === 0 ? (
                                <p className="text-sm text-base-content/60 py-3">
                                    Nenhuma outra filial disponível nesta organização.
                                </p>
                            ) : (
                                <select
                                    id="transfer-target"
                                    className="select select-bordered w-full"
                                    value={selectedTargetId}
                                    onChange={e =>
                                        setSelectedTargetId(
                                            e.target.value ? Number(e.target.value) : '',
                                        )
                                    }
                                >
                                    <option value="">Selecione uma filial</option>
                                    {transferTargets.map(target => (
                                        <option key={target.id} value={target.id}>
                                            {target.tradingName || target.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                            <label className="label pt-2">
                                <span className="label-text-alt text-base-content/50">
                                    Somente filiais da mesma organização.
                                </span>
                            </label>
                        </div>

                        <div className="alert alert-warning mt-4">
                            <MdWarningAmber className="text-xl shrink-0" />
                            <div className="text-sm">
                                <p className="font-semibold">
                                    Todo o histórico do parceiro vai junto.
                                </p>
                                <p className="mt-1">
                                    Agendas, salas, integrações, e-mails, contratos,
                                    cobranças, exames, consultas e procedimentos deste
                                    parceiro passam a pertencer à filial de destino. Os
                                    relatórios de produtividade e o faturamento das duas
                                    filiais serão alterados retroativamente. O catálogo e os
                                    preços do parceiro são mantidos.
                                </p>
                            </div>
                        </div>

                        <div className="modal-action mt-6">
                            <button
                                className="btn btn-ghost"
                                onClick={() => setShowTransferModal(false)}
                                disabled={transferring}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-secondary gap-2"
                                onClick={handleTransfer}
                                disabled={!selectedTargetId || transferring}
                            >
                                {transferring && (
                                    <span className="loading loading-spinner loading-sm" />
                                )}
                                {transferring ? 'Transferindo...' : 'Mover parceiro'}
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop bg-black/40"
                        onClick={() => !transferring && setShowTransferModal(false)}
                    />
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Excluir parceiro"
                message="Tem certeza de que deseja excluir este parceiro? Esta ação não pode ser desfeita."
                loading={deleting}
                confirmButtonText="Excluir"
            />

            <ConfirmModal
                isOpen={showRegenerateModal}
                onClose={() => setShowRegenerateModal(false)}
                onConfirm={handleRegenerateAccessCode}
                title="Gerar novo código de acesso"
                message="ATENÇÃO: Ao gerar um novo código, o código anterior será permanentemente invalidado. O parceiro perderá o acesso ao portal até receber o novo código. Deseja continuar?"
                loading={regenerating}
                confirmButtonText="Gerar novo código"
                confirmButtonClass="btn-warning"
            />
        </div>
    )
}
